import { ReactNode } from 'react';
import { ColumnsType, ColumnType } from 'antd/lib/table';

export interface ReportExportMetric {
  caption?: string;
  label: string;
  value: ReactNode;
}

export interface ReportExportTableData {
  header: string[];
  rows: (string | number)[][];
}

export const toReportExportCellValue = (value: ReactNode): string | number => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return value === undefined || value === null ? '' : String(value);
};

const getColumnDataIndexValue = <T>(
  record: T,
  dataIndex: ColumnType<T>['dataIndex'],
): unknown => {
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<unknown>(
      (value: unknown, key: string | number) =>
        (value as Record<string | number, unknown> | undefined)?.[key],
      record,
    );
  }

  return dataIndex === undefined
    ? undefined
    : (record as Record<string | number, unknown>)?.[
        dataIndex as string | number
      ];
};

const getColumnCellValue = <T>(
  column: ColumnType<T>,
  record: T,
  index: number,
): string | number => {
  const rawValue = getColumnDataIndexValue<T>(record, column.dataIndex);
  const renderedValue = column.render
    ? column.render(rawValue, record, index)
    : rawValue;

  return typeof renderedValue === 'string' || typeof renderedValue === 'number'
    ? renderedValue
    : toReportExportCellValue(rawValue as ReactNode);
};

export const getReportExportTableData = <T>(
  columns: ColumnsType<T>,
  rows: T[],
): ReportExportTableData => ({
  header: columns.map((column: ColumnType<T>) => column.title as string),
  rows: rows.map((record: T, index: number) =>
    columns.map((column: ColumnType<T>) =>
      getColumnCellValue<T>(column, record, index),
    ),
  ),
});
